'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import Image from 'next/image'

interface IntroAnimationProps {
  images: { src: string; name: string }[]
  onAnimationComplete?: () => void
  onExitStart?: () => void
}

// Detect mobile for performance optimization
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (navigator.maxTouchPoints > 0 && window.innerWidth < 768)
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Timing constants (in ms) - will be adjusted for mobile
const getTimingConstants = (isMobile: boolean) => {
  if (isMobile) {
    return {
      SCATTER_DURATION: 400,
      CIRCLE_START: 400,
      CIRCLE_FORM_DURATION: 500,
      EXIT_START: 2100,
      EXIT_DURATION: 1500,
      TOTAL_DURATION: 3600,
    }
  }
  return {
    SCATTER_DURATION: 600,
    CIRCLE_START: 600,
    CIRCLE_FORM_DURATION: 800,
    EXIT_START: 3400,
    EXIT_DURATION: 2500,
    TOTAL_DURATION: 5900,
  }
}

// Easing functions
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
}

// Deterministic pseudo-random
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 43758.5453
  return x - Math.floor(x)
}

export function IntroAnimation({
  images,
  onAnimationComplete,
  onExitStart,
}: IntroAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const logoRef = useRef<HTMLDivElement>(null)
  const [isComplete, setIsComplete] = useState(false)
  const [skipAnimation, setSkipAnimation] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  const startTimeRef = useRef<number | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const hasCompletedRef = useRef(false)
  const hasExitStartedRef = useRef(false)
  const isMobileRef = useRef(false)
  const timingRef = useRef(getTimingConstants(false))
  const containerSizeRef = useRef({ width: 0, height: 0 })

  // Keep callback refs up to date (avoids restarting animation on parent re-render)
  const onExitStartRef = useRef(onExitStart)
  const onCompleteRef = useRef(onAnimationComplete)
  onExitStartRef.current = onExitStart
  onCompleteRef.current = onAnimationComplete

  // Fibonacci disk distribution - even spread across hero area
  const scatterPositions = useMemo(() => {
    const count = images.length
    const goldenAngle = Math.PI * (3 - Math.sqrt(5))

    return images.map((_, i) => {
      const angle = i * goldenAngle
      const t = (i + 0.5) / count
      const radius = Math.sqrt(t) * 0.85

      // Deterministic jitter to break perfect pattern
      const jitterX = (seededRandom(i * 17 + 5) - 0.5) * 0.15
      const jitterY = (seededRandom(i * 31 + 9) - 0.5) * 0.15
      const randRot = (seededRandom(i * 41 + 11) - 0.5) * 50

      return {
        x: Math.cos(angle) * radius + jitterX,
        y: Math.sin(angle) * radius * 0.75 + jitterY,
        rotation: randRot,
      }
    })
  }, [images.length])

  // Detect mobile on mount
  useEffect(() => {
    const mobile = isMobileDevice()
    const reducedMotion = prefersReducedMotion()
    isMobileRef.current = mobile
    setIsMobile(mobile)
    timingRef.current = getTimingConstants(mobile)

    if (reducedMotion) {
      setSkipAnimation(true)
      onCompleteRef.current?.()
    }
  }, [])

  // Measure container
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const parent = containerRef.current.parentElement
        if (parent) {
          const rect = parent.getBoundingClientRect()
          if (rect.width > 0 && rect.height > 0) {
            containerSizeRef.current = { width: rect.width, height: rect.height }
            setIsReady(true)
          }
        }
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  // Animation loop - direct DOM manipulation, zero React re-renders per frame
  useEffect(() => {
    if (skipAnimation || !isReady) return

    const cards = cardRefs.current
    const totalCards = images.length
    const timing = timingRef.current
    let lastFrameTime = 0
    const frameInterval = isMobileRef.current ? 33 : 0 // 30fps throttle on mobile only

    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime
        lastFrameTime = currentTime
      }

      // Throttle on mobile
      if (frameInterval && currentTime - lastFrameTime < frameInterval) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }
      lastFrameTime = currentTime

      const elapsed = currentTime - startTimeRef.current
      const { width, height } = containerSizeRef.current
      const mobile = isMobileRef.current

      // Circle geometry
      const cardHalfWidth = mobile ? 50 : 75
      const maxRadius = Math.min(width / 2 - cardHalfWidth, height * 0.45, 320)
      const circleRadius = Math.max(maxRadius, 100)

      // Scatter scale - spread across hero area (not limited by circle radius)
      const scatterScaleX = width * 0.45
      const scatterScaleY = height * 0.55

      // Circle rotation
      let circleRotation = 0
      const rotateStart = timing.CIRCLE_START + timing.CIRCLE_FORM_DURATION
      if (elapsed > rotateStart) {
        circleRotation = -((elapsed - rotateStart) / 1000) * 60
      }

      // Determine phase
      let phase: 'scatter' | 'circle' | 'exit'
      if (elapsed < timing.SCATTER_DURATION) {
        phase = 'scatter'
      } else if (elapsed < timing.EXIT_START) {
        phase = 'circle'
      } else if (elapsed < timing.TOTAL_DURATION) {
        phase = 'exit'
        if (!hasExitStartedRef.current) {
          hasExitStartedRef.current = true
          onExitStartRef.current?.()
        }
      } else {
        // Complete
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true
          onCompleteRef.current?.()
          setIsComplete(true)
        }
        return
      }

      // Update logo opacity directly
      if (logoRef.current) {
        logoRef.current.style.opacity = phase === 'circle' ? '1' : '0'
      }

      // Update each card via direct DOM manipulation
      for (let i = 0; i < totalCards; i++) {
        const el = cards[i]
        if (!el) continue

        const scatterNorm = scatterPositions[i]
        const scatter = {
          x: scatterNorm.x * scatterScaleX,
          y: scatterNorm.y * scatterScaleY,
          rotation: scatterNorm.rotation,
        }

        const baseAngle = (i / totalCards) * 360 - 90
        const circleAngle = baseAngle + circleRotation
        const circleRad = (circleAngle * Math.PI) / 180
        const circleX = Math.cos(circleRad) * circleRadius
        const circleY = Math.sin(circleRad) * circleRadius

        let x: number, y: number, rotation: number, scale: number, opacity: number

        if (phase === 'scatter') {
          const progress = easeOutCubic(Math.min(elapsed / timing.SCATTER_DURATION, 1))
          x = scatter.x * (1 - progress * 0.3)
          y = scatter.y * (1 - progress * 0.3)
          rotation = scatter.rotation * (1 - progress * 0.5)
          scale = 0.4 + progress * 0.4
          opacity = progress
        } else if (phase === 'circle') {
          const formProgress = easeInOutCubic(
            Math.min((elapsed - timing.CIRCLE_START) / timing.CIRCLE_FORM_DURATION, 1)
          )
          // Start from scatter end values (scatter * 0.7 for pos, * 0.5 for rotation)
          const scatterEndX = scatter.x * 0.7
          const scatterEndY = scatter.y * 0.7
          const scatterEndRot = scatter.rotation * 0.5
          x = scatterEndX * (1 - formProgress) + circleX * formProgress
          y = scatterEndY * (1 - formProgress) + circleY * formProgress
          rotation = scatterEndRot * (1 - formProgress) + (circleAngle + 90) * formProgress
          scale = 0.8 + formProgress * 0.15
          opacity = 1
        } else {
          // exit
          const exitElapsed = elapsed - timing.EXIT_START
          const normalizedAngle = ((circleAngle % 360) + 360) % 360
          const exitOrder = normalizedAngle / 360
          const cardDelay = exitOrder * (timing.EXIT_DURATION * 0.6)
          const cardProgress = Math.max(0, Math.min(1, (exitElapsed - cardDelay) / (timing.EXIT_DURATION * 0.5)))
          const easedProgress = easeInOutQuad(cardProgress)

          const spiralAngle = circleAngle - easedProgress * 180
          const spiralRad = (spiralAngle * Math.PI) / 180
          const shrinkingRadius = circleRadius * (1 - easedProgress * 0.7)
          const spiralX = Math.cos(spiralRad) * shrinkingRadius
          const targetY = height * 0.7 + i * 25

          x = circleX * (1 - easedProgress * 0.7) + spiralX * easedProgress * 0.3
          y = circleY + (targetY - circleY) * easedProgress
          rotation = spiralAngle + 90
          scale = 0.95 * (1 - easedProgress * 0.4)
          opacity = 1 - easedProgress
        }

        el.style.transform = `translate3d(${x}px,${y}px,0) rotate(${rotation}deg) scale(${scale})`
        el.style.opacity = String(opacity)
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [skipAnimation, isReady, images.length, scatterPositions])

  if (isComplete || skipAnimation) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
      style={{ perspective: '1000px', opacity: isReady ? 1 : 0 }}
    >
      {/* Center point with logo */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          ref={logoRef}
          className="flex flex-col items-center gap-3 transition-opacity duration-500"
          style={{ opacity: 0 }}
        >
          <Image
            src="/logo-biomo-mini-light.svg"
            alt="Biomo"
            width={64}
            height={80}
            sizes="64px"
            className="h-16 sm:h-20 md:h-24 w-auto dark:hidden"
            priority
          />
          <Image
            src="/logo-biomo-mini.svg"
            alt="Biomo"
            width={64}
            height={80}
            sizes="64px"
            className="h-16 sm:h-20 md:h-24 w-auto hidden dark:block"
            priority
          />
          <span className="text-indigo-950 dark:text-white font-semibold text-2xl sm:text-3xl tracking-tight">
            Biomo
          </span>
        </div>
      </div>

      {/* Cards - only render on client to avoid hydration mismatch */}
      {isReady && (
        <div className="absolute inset-0 flex items-center justify-center">
          {images.map((image, index) => (
            <div
              key={index}
              ref={(el) => { cardRefs.current[index] = el }}
              className="absolute"
              style={{
                transform: 'translate3d(0,0,0) scale(0)',
                opacity: 0,
                willChange: isMobile ? 'auto' : 'transform, opacity',
              }}
            >
              <div className={`relative w-20 h-[45px] sm:w-28 sm:h-[63px] md:w-32 md:h-[72px] rounded-xl overflow-hidden shadow-xl border border-purple-500/30 dark:border-white/20 bg-white/10 ${isMobile ? '' : 'backdrop-blur-sm'}`}>
                <Image
                  src={image.src}
                  alt={image.name}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 80px, (max-width: 768px) 112px, 128px"
                  style={{ objectPosition: 'top' }}
                  priority={index < 8}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
